/**
 * Estado "XML do CT-e indisponível" da linha Tracking do painel de Expedição
 * (TMS-7192). Funções puras, sem Vue e sem terceiros — verificadas por
 * `tools/verificar-xml-cte-indisponivel.mjs`.
 *
 * O gatilho é `tp_desfecho` e, enquanto o XML segue vazio, `reenvio_permitido` diferente
 * de `true`. Payload sem a chave, com `null` ou com outro valor devolve `false` e a view
 * mantém o render legado (fail-open): é o que permite o front subir antes do backend.
 */

import { VALOR_AUSENTE } from "../constants/ocorrenciaIntegracaoCopy.js";
import {
  CHAMADO_DETALHES_TITULO,
  ROTULO_CARGA_ID,
  ROTULO_CHAMADO_CARGA,
  ROTULO_CHAMADO_CTE,
  ROTULO_CHAMADO_NOTA,
  ROTULO_CTES_LISTADOS,
  ROTULO_DOCCTE_ID,
  ROTULO_DOCFIS_ID,
  ROTULO_HORARIO_ULTIMA_TENTATIVA,
  TITULO,
} from "../constants/xmlCteIndisponivelCopy.js";
import { formatarDataHora } from "./ocorrenciaIntegracaoEstado.js";

/** Case `FalhouXmlCteAusente` do backend. */
export const TP_DESFECHO_XML_CTE_AUSENTE = "falhou_xml_cte_ausente";

function temValor(valor) {
  return valor !== null && valor !== undefined && String(valor).trim() !== "";
}

function texto(valor) {
  return temValor(valor) ? String(valor).trim() : "";
}

function detalhe(rotulo, valor) {
  return temValor(valor) ? [{ rotulo, valor: texto(valor) }] : [];
}

/** `ctes[]` do payload, tolerando ausência e lixo: só objetos entram. */
function ctesDoItem(item) {
  return item && Array.isArray(item.ctes)
    ? item.ctes.filter((cte) => cte && typeof cte === "object")
    : [];
}

function lista(ctes, chave) {
  return ctes
    .map((cte) => texto(cte[chave]))
    .filter(Boolean)
    .join(", ");
}

/**
 * O bloco só vale enquanto o reenvio é de fato inútil. Quando o XML já foi preenchido, o
 * backend passa a mandar `reenvio_permitido = true` para este mesmo desfecho (a nota
 * volta a ser elegível no "Reenviar"), e a copy do bloco — "Reenviar não resolve" —
 * passaria a mentir: a linha volta ao render de antes, com o ícone de reenvio da carga
 * habilitado. Só `true` estrito libera; ausente, `false`, `null` e qualquer outro valor
 * mantêm o bloco.
 */
export function isXmlCteIndisponivel(item) {
  return (
    Boolean(item) &&
    item.tp_desfecho === TP_DESFECHO_XML_CTE_AUSENTE &&
    item.reenvio_permitido !== true
  );
}

/**
 * O backend lista no máximo 10 CT-es por item agregado (`ctes[]`), mas `qtd_notas` traz o
 * total. Quando há mais notas do que CT-es listados, devolve "K de N notas" para a tela e o
 * chamado não passarem a impressão de que a lista é completa; senão, `null`.
 */
function resumoDaLista(item, ctes) {
  const total = item && item.qtd_notas;

  if (!Number.isInteger(total) || total <= ctes.length) {
    return null;
  }

  return `${ctes.length} de ${total} notas`;
}

export function detalhesTecnicos(item, cargaId) {
  const ctes = ctesDoItem(item);

  return [
    ...detalhe(ROTULO_CARGA_ID, cargaId),
    ...detalhe(ROTULO_DOCFIS_ID, lista(ctes, "docfis_id")),
    ...detalhe(ROTULO_DOCCTE_ID, lista(ctes, "doccte_id")),
    ...detalhe(ROTULO_CTES_LISTADOS, resumoDaLista(item, ctes)),
    ...detalhe(
      ROTULO_HORARIO_ULTIMA_TENTATIVA,
      formatarDataHora(item && item.dt_desfecho)
    ),
  ];
}

function descricaoDaNota(item) {
  const numero = texto(item.num_docfis);

  if (!numero) {
    return "";
  }

  return item.serie ? `${numero} - ${texto(item.serie)}` : numero;
}

/**
 * Bloco que vai para a área de transferência em "Abrir chamado": carga, nota e
 * CT-e — os itens que a copy aprovada manda informar — mais os detalhes técnicos.
 * Texto puro; a view o entrega ao clipboard, nunca ao DOM.
 */
export function textoParaChamado(item, cargaId) {
  if (!item) {
    return "";
  }

  const ctes = ctesDoItem(item);
  const resumo = resumoDaLista(item, ctes);
  const cte = lista(ctes, "num_cte") || VALOR_AUSENTE;

  const linhas = [
    TITULO,
    "",
    `${ROTULO_CHAMADO_CARGA}: ${texto(cargaId) || VALOR_AUSENTE}`,
    `${ROTULO_CHAMADO_NOTA}: ${descricaoDaNota(item) || VALOR_AUSENTE}`,
    `${ROTULO_CHAMADO_CTE}: ${resumo ? `${cte} (${resumo})` : cte}`,
  ];

  const detalhes = detalhesTecnicos(item, cargaId);

  if (detalhes.length > 0) {
    linhas.push("", CHAMADO_DETALHES_TITULO);
    detalhes.forEach((linha) => {
      linhas.push(`- ${linha.rotulo}: ${linha.valor}`);
    });
  }

  return linhas.join("\n");
}

/**
 * Id do `b-collapse` dos detalhes. Único por item dentro da carga (o item é uma
 * nota no ramo detalhado e um grupo no agregado — `num_docfis` + `serie` os
 * distinguem) e sempre um id DOM válido: os valores chegam sem garantia de forma.
 */
export function idDetalhes(item, cargaId) {
  const nota = item || {};

  return `xml-cte-detalhes-${texto(cargaId)}-${texto(nota.num_docfis)}-${texto(
    nota.serie
  )}`.replace(/[^A-Za-z0-9_-]/g, "_");
}
